import { useEffect, useId, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { motion } from 'framer-motion'

import DDIcon from '../../icons/DDIcon'
import CountryFlag from './CountryFlag'
import DropdownOverlay from './DropdownOverlay'
import useOptionListKeyDown from './useOptionListKeyDown'
import { useReducedMotionPreference, DURATION } from '../../../styles/motion'
import styles from './PhoneInput.module.css'

// Spec §5.3 — dial-code list. The digits field holds the national subscriber number
// (no trunk "0" prefix, since +234 is already shown by the dial-code selector) - NG is
// 10 digits (803 201 1234); other countries just display raw digits since no grouping/
// length was specified for them.
export const PHONE_COUNTRIES = [
  { code: 'NG', name: 'Nigeria', dial: '+234', digitLength: 10, groups: [3, 3, 4] },
  { code: 'GH', name: 'Ghana', dial: '+233', digitLength: null, groups: null },
  { code: 'KE', name: 'Kenya', dial: '+254', digitLength: null, groups: null },
  { code: 'ZA', name: 'South Africa', dial: '+27', digitLength: null, groups: null },
  { code: 'GB', name: 'UK', dial: '+44', digitLength: null, groups: null },
  { code: 'US', name: 'US', dial: '+1', digitLength: null, groups: null },
]

const formatDigits = (digits, groups) => {
  if (!groups) return digits
  const parts = []
  let i = 0
  for (const size of groups) {
    if (i >= digits.length) break
    parts.push(digits.slice(i, i + size))
    i += size
  }
  return parts.join(' ')
}

// Spec §5.3 — left segment (flag + dial code + caret) opens a 236px list; phone digits
// auto-format as the user types. `onChange` receives the raw digit string; formatting is
// display-only.
const PhoneInput = ({ label, required, country, digits, onCountryChange, onDigitsChange, error, disabled }) => {
  const inputId = useId()
  const helperId = `${inputId}-helper`
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const reduceMotion = useReducedMotionPreference()
  const handlePanelKeyDown = useOptionListKeyDown(panelRef)

  // Spec §10 - focus the selected country as soon as the list opens.
  useEffect(() => {
    if (!dropdownOpen) return undefined
    const timer = setTimeout(() => {
      panelRef.current?.querySelector('[aria-selected="true"]')?.focus()
    }, 0)
    return () => clearTimeout(timer)
  }, [dropdownOpen])

  const selected = PHONE_COUNTRIES.find((c) => c.code === country) ?? PHONE_COUNTRIES[0]

  const handleDigitsChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '')
    const capped = selected.digitLength ? raw.slice(0, selected.digitLength) : raw
    onDigitsChange(capped)
  }

  const rowClasses = [styles.row, focused && styles.focused, error && styles.error, disabled && styles.disabled]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <div className={rowClasses}>
        <button
          type='button'
          ref={triggerRef}
          className={styles.dialTrigger}
          aria-haspopup='listbox'
          aria-expanded={dropdownOpen}
          disabled={disabled}
          onClick={() => setDropdownOpen((open) => !open)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && !dropdownOpen) {
              e.preventDefault()
              setDropdownOpen(true)
            }
          }}
        >
          <span className={styles.flag}>
            <CountryFlag code={selected.code} />
          </span>
          <span>{selected.dial}</span>
          <DDIcon name='select-caret-dial' className={dropdownOpen ? styles.caretOpen : undefined} />
        </button>
        <input
          id={inputId}
          type='tel'
          inputMode='numeric'
          autoComplete='tel-national'
          className={styles.digits}
          placeholder='803 201 1234'
          value={formatDigits(digits, selected.groups)}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? helperId : undefined}
          onChange={handleDigitsChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </div>
      {error && (
        <div id={helperId} className={[styles.helper, styles.errorText].join(' ')}>
          <DDIcon name='helper-error' />
          <span>{error}</span>
        </div>
      )}
      <DropdownOverlay
        triggerRef={triggerRef}
        isOpen={dropdownOpen}
        onClose={() => setDropdownOpen(false)}
        matchWidth={false}
      >
        <motion.div
          ref={panelRef}
          role='listbox'
          className={styles.list}
          initial={reduceMotion ? false : { opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: DURATION.dropdown }}
          onKeyDown={handlePanelKeyDown}
        >
          {PHONE_COUNTRIES.map((c) => (
            <button
              key={c.code}
              type='button'
              role='option'
              aria-selected={c.code === selected.code}
              className={styles.listItem}
              onClick={() => {
                onCountryChange(c.code)
                setDropdownOpen(false)
              }}
            >
              <span className={styles.flag}>
                <CountryFlag code={c.code} />
              </span>
              <span className={styles.listItemName}>{c.name}</span>
              <span className={styles.listItemDial}>{c.dial}</span>
              {c.code === selected.code && <DDIcon name='select-check' />}
            </button>
          ))}
        </motion.div>
      </DropdownOverlay>
    </div>
  )
}

PhoneInput.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  country: PropTypes.string,
  digits: PropTypes.string.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  onDigitsChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
}

PhoneInput.defaultProps = {
  country: 'NG',
  required: false,
  disabled: false,
}

export default PhoneInput
